import React, { useState, useEffect } from 'react';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';

/**
 * RecoverySheet — listens for deep-link recovery events and shows a Sheet.
 * Rendered at the app root so it's always available regardless of current route.
 */
const RecoverySheet: React.FC = () => {
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleSuccess = () => setSuccessOpen(true);
    const handleError = () => setErrorOpen(true);

    window.addEventListener('helm:recovery-success', handleSuccess);
    window.addEventListener('helm:recovery-error', handleError);

    return () => {
      window.removeEventListener('helm:recovery-success', handleSuccess);
      window.removeEventListener('helm:recovery-error', handleError);
    };
  }, []);

  const handleGoToSettings = () => {
    setSuccessOpen(false);
    navigate('/settings/privacy');
  };

  const handleStartRecoveryAgain = () => {
    setErrorOpen(false);
    window.open('https://helmapp.co/account/app-lock-recovery', '_blank');
  };

  return (
    <>
      {/* Recovery success */}
      <Sheet
        open={successOpen}
        onDismiss={() => setSuccessOpen(false)}
        title="App lock cleared"
      >
        <div className="flex flex-col gap-4 p-1">
          <p className="text-[var(--theme-text-secondary)] text-sm">
            Your PIN has been removed. Set a new PIN in Settings &gt; Privacy &amp; Security
            whenever you&apos;re ready.
          </p>
          <div className="flex gap-3 mt-2">
            <Button
              variant="secondary"
              onClick={handleGoToSettings}
              className="flex-1"
            >
              Go to Settings
            </Button>
            <Button
              variant="primary"
              onClick={() => setSuccessOpen(false)}
              className="flex-1"
            >
              Continue
            </Button>
          </div>
        </div>
      </Sheet>

      {/* Recovery error */}
      <Sheet
        open={errorOpen}
        onDismiss={() => setErrorOpen(false)}
        title="Recovery link expired"
      >
        <div className="flex flex-col gap-4 p-1">
          <p className="text-[var(--theme-text-secondary)] text-sm">
            This link has already been used or has expired. Recovery links expire after
            15 minutes.
          </p>
          <Button
            variant="primary"
            onClick={handleStartRecoveryAgain}
          >
            Start recovery again
          </Button>
        </div>
      </Sheet>
    </>
  );
};

export default RecoverySheet;
